import React, { useRef, useState } from 'react';
import type { FC } from 'react';
import styled from 'styled-components';

import { useIntersectionObserver } from 'usehooks-ts';
import { useTranslation } from '@lt/localization';

// Components:
import { ButtonTs } from '@lt/components';

// Models:
import type { ICountryListMobile } from './Models/country-list.model';

export const CountryListMobile: FC<ICountryListMobile> = (props) => {
  const {
    className,
    linkedCountryList,
    linkedCountryListTitle,
    trackCountryLinkClick,
    trackCountryListExpand,
  } = props;

  const { t } = useTranslation();

  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(ref, {
    freezeOnceVisible: true,
  });
  const isVisible = !!entry?.isIntersecting || !linkedCountryListTitle;

  const [showAll, setShowAll] = useState(false);
  const handleShowMore = () => {
    trackCountryListExpand();
    setShowAll(!showAll);
  };

  let listCountOffset = linkedCountryList.length;
  const mobileCountOffset = 6;
  listCountOffset = showAll ? linkedCountryList.length : mobileCountOffset;

  return (
    <StyledContainer $isWhite={!linkedCountryListTitle}>
      {!!linkedCountryListTitle && (
        <StyledName>{linkedCountryListTitle}</StyledName>
      )}
      <StyledCountryList className={className} ref={ref}>
        {linkedCountryList.map((item, index) => {
          const { link, text, iso2 } = item;

          return index < listCountOffset ? (
            <ListItem key={`${link}`}>
              {isVisible && (
                <StyledFlag
                  className={`lt-flag lt-flag-${iso2?.toLowerCase() || 'us'}`}
                />
              )}

              <Anchor href={link} onClick={() => trackCountryLinkClick(iso2)}>
                {text}
              </Anchor>
            </ListItem>
          ) : null;
        })}
      </StyledCountryList>
      {!showAll ? (
        <StyledShowMore variant="functional" onClick={handleShowMore}>
          {t('explore.links.coutryList.showMoreButton')}
        </StyledShowMore>
      ) : null}
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $isWhite: boolean }>`
  ${({ $isWhite }) =>
    $isWhite &&
    `
    background-color: #fff;
    border-radius: 4px 0px 0px 4px;
    padding: 8px 16px;
  `}
`;

const StyledCountryList = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledName = styled.h2`
  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
  margin-bottom: 8px;
`;

const ListItem = styled.div`
  align-items: center;
  margin: 4px 0;
  display: flex;
  justify-content: start;
  flex-basis: 50%;
`;

const StyledFlag = styled.div`
  margin-right: 4px;
`;

const Anchor = styled.a`
  font-size: 14px;
  line-height: 16px;
  text-decoration: none;
  color: #17181a;
`;

const StyledShowMore = styled(ButtonTs)`
  color: #007aff;
`;
